import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { VendorCard } from '@/components/VendorCard';
import { GuideTopNavigation } from '@/components/GuideTopNavigation';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ELOPE_NAV_ITEMS = [
  {
    id: 'getting-married',
    label: 'Getting Married',
    to: '/getting-married-in-gibraltar',
    activePathname: '/getting-married-in-gibraltar',
  },
  {
    id: 'how-to-get-married',
    label: 'How to Get Married',
    to: '/how-to-get-married-in-gibraltar',
    activePathname: '/how-to-get-married-in-gibraltar',
  },
  {
    id: 'cost-of-getting-married',
    label: 'Cost of Getting Married',
    to: '/cost-of-getting-married-in-gibraltar',
    activePathname: '/cost-of-getting-married-in-gibraltar',
  },
  {
    id: 'elopements',
    label: 'Elopements',
    to: '/elope-in-gibraltar',
    activePathname: '/elope-in-gibraltar',
  },
  {
    id: 'wedding-planners',
    label: 'Wedding Planners',
    to: '/getting-married-in-gibraltar#wedding-planner',
    activePathname: null,
  },
];

const createVendorSearchText = (vendor) => {
  const values = [];
  if (vendor.name) values.push(vendor.name);
  if (vendor.description) values.push(vendor.description);
  if (vendor.category) values.push(vendor.category);
  if (Array.isArray(vendor.categories)) values.push(...vendor.categories);
  if (Array.isArray(vendor.services)) values.push(...vendor.services);
  if (Array.isArray(vendor.tags)) values.push(...vendor.tags);
  return values.join(' ').toLowerCase();
};

const hasAnyTerm = (text, terms) => terms.some((term) => text.includes(term));

export default function ElopeInGibraltarPage() {
  const [allVendors, setAllVendors] = useState([]);

  useEffect(() => {
    document.title = 'Elope in Gibraltar | Elopement Packages & Wedding Planners';

    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute(
        'content',
        'Discover how to elope in Gibraltar with trusted wedding planners, all-in-one elopement packages, and local suppliers for a stress-free ceremony.',
      );
    }

    const loadVendors = async () => {
      try {
        const response = await axios.get(`${API}/vendors`);
        setAllVendors(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        console.error('Failed to load vendors for elopement page', error);
      }
    };

    loadVendors();
  }, []);

  const plannerPackageVendors = useMemo(() => {
    const plannerTerms = ['wedding planner', 'wedding planners', 'planner', 'planning'];
    const elopementTerms = ['elope', 'elopement'];
    const packageTerms = ['all in one', 'all-in-one', 'package', 'registry package', 'registry packages', 'bundled'];

    const withSearchText = allVendors.map((vendor) => ({
      vendor,
      searchText: createVendorSearchText(vendor),
    }));

    const preferred = withSearchText
      .filter((entry) => {
        const plannerMatch = hasAnyTerm(entry.searchText, plannerTerms);
        const packageMatch = hasAnyTerm(entry.searchText, [...elopementTerms, ...packageTerms]);
        return plannerMatch && packageMatch;
      })
      .map((entry) => entry.vendor);

    const fallback = withSearchText
      .filter((entry) => hasAnyTerm(entry.searchText, plannerTerms) || hasAnyTerm(entry.searchText, [...elopementTerms, ...packageTerms]))
      .map((entry) => entry.vendor);

    const finalResults = preferred.length > 0 ? preferred : (fallback.length > 0 ? fallback : allVendors);
    return finalResults.slice(0, 6);
  }, [allVendors]);

  return (
    <div className="min-h-screen bg-[linear-gradient(180deg,#faf6f0_0%,#fffdf9_40%,#ffffff_100%)] py-12 md:py-16" data-testid="elope-gibraltar-page">
      <article className="container-wedding max-w-5xl space-y-8 md:space-y-10" data-testid="elope-gibraltar-article">
        <header className="space-y-5" data-testid="elope-gibraltar-header">
          <p className="caption text-[#8f6b00]" data-testid="elope-gibraltar-kicker">Elopement Packages &amp; Wedding Planners</p>
          <h1 className="heading-xl" data-testid="elope-gibraltar-h1">Elope in Gibraltar</h1>

          <GuideTopNavigation testIdPrefix="elope-guide-top-navigation" items={ELOPE_NAV_ITEMS} />

          <div className="space-y-4 text-base text-stone-700 leading-relaxed" data-testid="elope-gibraltar-intro">
            <p>
              Gibraltar is one of the most popular destinations in the world for couples looking to elope. With a simple legal process, stunning locations, and the option to organise everything quickly, it&apos;s the perfect choice for a stress-free wedding experience.
            </p>
            <p>
              Many couples choose to work with wedding planners or book all-in-one elopement packages, allowing them to arrange their entire day — from legal requirements to photography and styling — with ease.
            </p>
          </div>

          <div className="overflow-hidden rounded-xl border border-[#D4AF37]/40 bg-white shadow-[0_20px_50px_rgba(28,25,23,0.08)]" data-testid="elope-gibraltar-hero-image-wrap">
            <img
              src="/images/gibraltar-elopement-ceremony.jpg"
              alt="Intimate elopement ceremony in Gibraltar with coastal and Rock backdrop"
              className="h-auto w-full"
              loading="eager"
              width="1400"
              height="900"
              data-testid="elope-gibraltar-hero-image"
            />
          </div>
        </header>

        <section className="space-y-8" data-testid="elope-gibraltar-sections">
          <section className="rounded-xl border border-[#D4AF37]/35 bg-white p-6 md:p-8 shadow-[0_12px_30px_rgba(28,25,23,0.04)]" data-testid="elope-section-why">
            <h2 className="text-3xl font-semibold text-[#5C0120] mb-4">Why elope in Gibraltar?</h2>
            <ul className="space-y-2 text-stone-700 leading-relaxed list-disc pl-6" data-testid="elope-why-list">
              <li>Simple and fast legal process</li>
              <li>No residency requirements</li>
              <li>Ideal for international couples</li>
              <li>Beautiful locations for intimate ceremonies</li>
            </ul>
          </section>

          <section className="rounded-xl border border-[#D4AF37]/35 bg-white p-6 md:p-8 shadow-[0_12px_30px_rgba(28,25,23,0.04)]" data-testid="elope-section-packages-planners">
            <h2 className="text-3xl font-semibold text-[#5C0120] mb-4">Elopement packages and wedding planners in Gibraltar</h2>
            <p className="text-stone-700 leading-relaxed mb-4">
              For couples looking for a seamless experience, many wedding planners in Gibraltar offer complete elopement packages. These typically include:
            </p>
            <ul className="space-y-2 text-stone-700 leading-relaxed list-disc pl-6 mb-4" data-testid="elope-packages-list">
              <li>Legal arrangements and documentation</li>
              <li>Ceremony coordination</li>
              <li>Photography and videography</li>
              <li>Flowers and styling</li>
              <li>On-the-day coordination</li>
            </ul>
            <p className="text-stone-700 leading-relaxed mb-6">
              Working with a planner allows you to organise everything quickly and with minimal stress, especially if you are travelling from abroad.
            </p>

            <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3" data-testid="elope-planner-vendor-cards-grid">
              {plannerPackageVendors.map((vendor) => (
                <VendorCard key={vendor.id} vendor={vendor} />
              ))}
            </div>

            <div className="mt-5" data-testid="elope-planner-vendor-cards-cta-wrap">
              <Link
                to="/planners-gibraltar"
                className="inline-flex items-center justify-center rounded-xl border border-[#5C0120] bg-[#5C0120] px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-[#D4AF37] hover:border-[#D4AF37]"
                data-testid="elope-planner-vendor-cards-cta-link"
              >
                View all elopement planners &amp; packages
              </Link>
            </div>
          </section>

          <section className="rounded-xl border border-[#D4AF37]/35 bg-white p-6 md:p-8 shadow-[0_12px_30px_rgba(28,25,23,0.04)]" data-testid="elope-section-how-to">
            <h2 className="text-3xl font-semibold text-[#5C0120] mb-4">How to elope in Gibraltar</h2>
            <p className="text-stone-700 leading-relaxed mb-4">Eloping in Gibraltar is straightforward:</p>
            <ol className="space-y-2 text-stone-700 leading-relaxed list-decimal pl-6 mb-4" data-testid="elope-how-to-list">
              <li>Choose your date</li>
              <li>Book your ceremony</li>
              <li>Prepare your documents</li>
              <li>Travel to Gibraltar</li>
              <li>Get married</li>
            </ol>
            <p className="text-stone-700 leading-relaxed">
              For a detailed step-by-step guide, see:{' '}
              <Link to="/how-to-get-married-in-gibraltar" className="text-[#5C0120] hover:underline" data-testid="elope-link-how-to-guide">
                /how-to-get-married-in-gibraltar
              </Link>
            </p>
          </section>

          <section className="rounded-xl border border-[#D4AF37]/35 bg-white p-6 md:p-8 shadow-[0_12px_30px_rgba(28,25,23,0.04)]" data-testid="elope-section-best-places">
            <h2 className="text-3xl font-semibold text-[#5C0120] mb-4">Best places to elope in Gibraltar</h2>
            <ul className="space-y-2 text-stone-700 leading-relaxed list-disc pl-6" data-testid="elope-best-places-list">
              <li>Gibraltar Registry Office</li>
              <li>Scenic outdoor locations</li>
              <li>Private venues and hotels</li>
            </ul>
          </section>

          <section className="rounded-xl border border-[#D4AF37]/35 bg-white p-6 md:p-8 shadow-[0_12px_30px_rgba(28,25,23,0.04)]" data-testid="elope-section-costs">
            <h2 className="text-3xl font-semibold text-[#5C0120] mb-4">How much does it cost to elope in Gibraltar?</h2>
            <p className="text-stone-700 leading-relaxed mb-4">
              Elopements in Gibraltar can range from simple legal ceremonies to fully planned experiences with packages.
            </p>
            <p className="text-stone-700 leading-relaxed mb-4">Costs vary depending on:</p>
            <ul className="space-y-2 text-stone-700 leading-relaxed list-disc pl-6 mb-4" data-testid="elope-costs-list">
              <li>Whether you use a planner</li>
              <li>The services included</li>
              <li>Your chosen location</li>
            </ul>
            <p className="text-stone-700 leading-relaxed">
              For a full breakdown, see:{' '}
              <Link to="/cost-of-getting-married-in-gibraltar" className="text-[#5C0120] hover:underline" data-testid="elope-link-cost-guide">
                /cost-of-getting-married-in-gibraltar
              </Link>
            </p>
          </section>

          <section className="rounded-xl border border-[#D4AF37]/35 bg-white p-6 md:p-8 shadow-[0_12px_30px_rgba(28,25,23,0.04)]" data-testid="elope-section-faqs">
            <h2 className="text-3xl font-semibold text-[#5C0120] mb-5">FAQs about eloping in Gibraltar</h2>

            <div className="space-y-5" data-testid="elope-faq-items">
              <div data-testid="elope-faq-without-guests">
                <h3 className="text-xl font-semibold text-stone-900 mb-2">Can we elope in Gibraltar without guests?</h3>
                <p className="text-stone-700 leading-relaxed">Yes, many couples choose to have a private ceremony with just witnesses.</p>
              </div>

              <div data-testid="elope-faq-through-planner">
                <h3 className="text-xl font-semibold text-stone-900 mb-2">Can everything be arranged through a planner?</h3>
                <p className="text-stone-700 leading-relaxed">Yes, many Gibraltar wedding planners offer full packages that include all elements of the day.</p>
              </div>

              <div data-testid="elope-faq-how-quickly">
                <h3 className="text-xl font-semibold text-stone-900 mb-2">How quickly can we elope in Gibraltar?</h3>
                <p className="text-stone-700 leading-relaxed">In many cases, elopements can be arranged within a few weeks.</p>
              </div>
            </div>
          </section>

          <section className="rounded-xl border border-[#D4AF37]/35 bg-white p-6 md:p-8 shadow-[0_12px_30px_rgba(28,25,23,0.04)]" data-testid="elope-section-planning">
            <h2 className="text-3xl font-semibold text-[#5C0120] mb-4">Planning your elopement in Gibraltar</h2>
            <p className="text-stone-700 leading-relaxed mb-4">
              Once you decide to elope, choosing the right suppliers will help bring everything together.
            </p>
            <p className="text-stone-700 leading-relaxed mb-4">Explore:</p>
            <ul className="space-y-2 text-stone-700 leading-relaxed list-disc pl-6" data-testid="elope-planning-links-list">
              <li>
                <Link to="/photographers-gibraltar" className="text-[#5C0120] hover:underline" data-testid="elope-link-photographers">
                  Wedding photographers in Gibraltar
                </Link>
              </li>
              <li>
                <Link to="/florists-gibraltar" className="text-[#5C0120] hover:underline" data-testid="elope-link-florists">
                  Wedding florists in Gibraltar
                </Link>
              </li>
              <li>
                <Link to="/planners-gibraltar" className="text-[#5C0120] hover:underline" data-testid="elope-link-planners">
                  Wedding planners and packages
                </Link>
              </li>
            </ul>

            <p className="text-stone-700 leading-relaxed mt-4">
              You can also review the complete legal overview:{' '}
              <Link to="/getting-married-in-gibraltar" className="text-[#5C0120] hover:underline" data-testid="elope-link-getting-married-guide">
                /getting-married-in-gibraltar
              </Link>
              .
            </p>
          </section>
        </section>
      </article>
    </div>
  );
}
